import {
  contrastWarnings,
  customCssSchema,
  effectivePalette,
  EMPTY_PALETTE,
  FONT_OPTIONS,
  fontStack,
  HEARTH_DEFAULTS,
  MAX_CUSTOM_CSS,
  PALETTE_KEYS,
  PALETTE_LABELS,
  PALETTE_PRESETS,
  RADIUS_OPTIONS,
  unreadablePairs,
  type ContrastWarning,
  type FontId,
  type PaletteKey,
  type ThemeCustomisation,
} from '@hearth/shared';
import { createFileRoute } from '@tanstack/react-router';
import { CircleAlert, Moon, RotateCcw, Sun, TriangleAlert } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { ColourField } from '@/components/customisation/colour-field';
import { ThemePreview } from '@/components/customisation/theme-preview';
import {
  ControlSection,
  PreviewFrame,
  StudioPanes,
} from '@/components/customisation/theme-studio-panes';
import { FormField } from '@/components/form-field';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { displayName, pickLogo } from '@/lib/customisation/logos';
import { publicSiteFromDraft } from '@/lib/customisation/site';
import { useSiteDraft } from '@/lib/customisation/site-draft';
import { useAuthenticatedSession } from '@/lib/session';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_app/admin/customisation/theme')({ component: Page });

type Scheme = 'light' | 'dark';
type Appearance = ThemeCustomisation['appearance'];

/** Palette keys grouped the way a designer thinks about them. */
const PALETTE_GROUPS: { title: string; keys: PaletteKey[] }[] = [
  { title: 'Accents', keys: ['primary', 'secondary'] },
  { title: 'Surfaces', keys: ['background', 'sidebar', 'card', 'elevated', 'border'] },
  { title: 'Text', keys: ['foreground', 'mutedForeground'] },
  { title: 'Status', keys: ['success', 'warning', 'destructive', 'info'] },
];

const RADIUS_LABELS: Record<(typeof RADIUS_OPTIONS)[number], string> = {
  none: 'Sharp',
  sm: 'Small',
  md: 'Medium',
  lg: 'Large',
  xl: 'Extra large',
};

function Page() {
  const draft = useSiteDraft();
  const session = useAuthenticatedSession();
  const [scheme, setScheme] = useState<Scheme>('dark');
  const { theme } = draft.site;
  const palette = theme[scheme];
  const defaults = HEARTH_DEFAULTS[scheme];
  const warnings = contrastWarnings(theme);
  const errors = unreadablePairs(theme);
  const errorKeys = new Set(errors.map((w) => `${w.scheme}:${w.pair.join(':')}`));
  const schemeWarnings = warnings.filter((w) => w.scheme === scheme);
  const customised = PALETTE_KEYS.some((k) => palette[k] !== null);
  const site = publicSiteFromDraft(draft.site);

  const setColour = (key: PaletteKey, value: string | null) =>
    draft.update((s) => {
      s.theme[scheme][key] = value;
    }, `Change ${PALETTE_LABELS[key]}`);
  const setAppearance = <K extends keyof Appearance>(key: K, value: Appearance[K]) =>
    draft.update((s) => {
      s.theme.appearance[key] = value;
    }, `Change ${key}`);
  const setFont = (key: keyof ThemeCustomisation['typography'], value: FontId) =>
    draft.update((s) => {
      s.theme.typography[key] = value;
    }, `Change ${key} font`);
  const applyPreset = (preset: (typeof PALETTE_PRESETS)[number]) =>
    draft.update((s) => {
      s.theme.light = { ...EMPTY_PALETTE, ...preset.light };
      s.theme.dark = { ...EMPTY_PALETTE, ...preset.dark };
    }, `Apply the ${preset.label} palette`);

  const cssCheck = customCssSchema.safeParse(draft.site.customCss);
  const cssError = cssCheck.success ? undefined : cssCheck.error.issues[0]?.message;

  const controls = (
    <>
      <ControlSection
        title="Start from a palette"
        description="A preset sets the main colours for both schemes; adjust anything afterwards."
      >
        <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {PALETTE_PRESETS.map((preset) => (
            <li key={preset.id}>
              <PresetSwatch
                label={preset.label}
                light={effectivePalette({ ...EMPTY_PALETTE, ...preset.light }, 'light')}
                dark={effectivePalette({ ...EMPTY_PALETTE, ...preset.dark }, 'dark')}
                onClick={() => applyPreset(preset)}
              />
            </li>
          ))}
        </ul>
      </ControlSection>

      <ControlSection
        title="Colours"
        description="Each colour is a hex value or the Hearth default. Related tokens (button text, focus rings, popovers) follow automatically."
        actions={
          <Button
            type="button"
            size="xs"
            variant="ghost"
            disabled={!customised}
            onClick={() =>
              draft.update((s) => {
                s.theme[scheme] = { ...EMPTY_PALETTE };
              }, `Reset ${scheme} colours`)
            }
          >
            <RotateCcw /> Reset {scheme} to Hearth defaults
          </Button>
        }
      >
        <Tabs value={scheme} onValueChange={(v) => setScheme(v as Scheme)}>
          <TabsList className="w-full">
            <TabsTrigger value="dark">
              <Moon /> Dark
            </TabsTrigger>
            <TabsTrigger value="light">
              <Sun /> Light
            </TabsTrigger>
          </TabsList>
        </Tabs>
        {schemeWarnings.length > 0 && (
          <ul className="grid gap-1.5" aria-label="Contrast checks">
            {schemeWarnings.map((w) => (
              <ContrastNotice
                key={w.pair.join(':')}
                warning={w}
                error={errorKeys.has(`${w.scheme}:${w.pair.join(':')}`)}
              />
            ))}
          </ul>
        )}
        <div className="grid gap-5">
          {PALETTE_GROUPS.map((group) => (
            <div key={group.title} className="grid gap-3">
              <p className="text-subtle text-[0.6875rem] font-semibold tracking-[0.08em] uppercase">
                {group.title}
              </p>
              {group.keys.map((key) => (
                <ColourField
                  key={`${scheme}-${key}`}
                  label={PALETTE_LABELS[key]}
                  value={palette[key]}
                  fallback={defaults[key]}
                  onChange={(v) => setColour(key, v)}
                />
              ))}
            </div>
          ))}
        </div>
      </ControlSection>

      <ControlSection
        title="Typography"
        description="Bundled fonts and system stacks only; nothing is loaded from another site."
      >
        <div className="grid gap-4">
          <FontSelect
            label="Headings"
            value={theme.typography.heading}
            kinds={['sans', 'serif']}
            onChange={(v) => setFont('heading', v)}
            sample="Your servers, your rules"
          />
          <FontSelect
            label="Body text"
            value={theme.typography.body}
            kinds={['sans', 'serif']}
            onChange={(v) => setFont('body', v)}
            sample="Paper 1.21 · 12 of 20 players online · backup due tonight"
          />
          <FontSelect
            label="Console and code"
            value={theme.typography.mono}
            kinds={['mono']}
            onChange={(v) => setFont('mono', v)}
            sample={'[12:04:15 INFO]: Done (3.2s)! For help, type "help"'}
          />
        </div>
      </ControlSection>

      <ControlSection title="Appearance">
        <div className="grid gap-5">
          <Choice
            label="Corner radius"
            value={theme.appearance.radius}
            options={RADIUS_OPTIONS.map((r) => ({ value: r, label: RADIUS_LABELS[r] }))}
            onChange={(v) => setAppearance('radius', v)}
          />
          <Choice
            label="Buttons"
            value={theme.appearance.buttonStyle}
            options={[
              { value: 'default', label: 'Rounded' },
              { value: 'pill', label: 'Pill' },
              { value: 'square', label: 'Square' },
            ]}
            onChange={(v) => setAppearance('buttonStyle', v)}
          />
          <Choice
            label="Cards"
            value={theme.appearance.cardStyle}
            options={[
              { value: 'default', label: 'Default' },
              { value: 'flat', label: 'Flat' },
              { value: 'elevated', label: 'Elevated' },
            ]}
            onChange={(v) => setAppearance('cardStyle', v)}
          />
          <Choice
            label="Navigation highlight"
            value={theme.appearance.navStyle}
            options={[
              { value: 'default', label: 'Default' },
              { value: 'tinted', label: 'Tinted' },
              { value: 'bold', label: 'Bold' },
            ]}
            onChange={(v) => setAppearance('navStyle', v)}
          />
          <Choice
            label="Sidebar density"
            value={theme.appearance.sidebarDensity}
            options={[
              { value: 'comfortable', label: 'Comfortable' },
              { value: 'compact', label: 'Compact' },
            ]}
            onChange={(v) => setAppearance('sidebarDensity', v)}
          />
          <Choice
            label="Page density"
            value={theme.appearance.density}
            options={[
              { value: 'comfortable', label: 'Comfortable' },
              { value: 'compact', label: 'Compact' },
            ]}
            onChange={(v) => setAppearance('density', v)}
          />
          <Choice
            label="Default theme"
            description="What a visitor gets before they choose one."
            value={theme.appearance.defaultTheme}
            options={[
              { value: 'dark', label: 'Dark' },
              { value: 'light', label: 'Light' },
              { value: 'system', label: 'Follow the device' },
            ]}
            onChange={(v) => setAppearance('defaultTheme', v)}
          />
          <div className="flex items-center justify-between gap-4">
            <div className="grid gap-0.5">
              <Label htmlFor="allow-theme-switch">Let customers switch themes</Label>
              <p className="text-muted-foreground text-xs">
                Off hides the theme menu and pins the default for everyone.
              </p>
            </div>
            <Switch
              id="allow-theme-switch"
              checked={theme.appearance.allowThemeSwitch}
              onCheckedChange={(v) => setAppearance('allowThemeSwitch', v)}
            />
          </div>
        </div>
      </ControlSection>

      <ControlSection
        title="Advanced: custom CSS"
        description={
          <>
            For the last few details a theme can’t express. It applies to the customer panel only
            (never to admin pages), is scoped to it, and is limited to {MAX_CUSTOM_CSS / 1000} KB.{' '}
            <code>@import</code> and <code>url()</code> to other sites are refused.
          </>
        }
      >
        <div className="bg-warning/10 text-foreground flex gap-2 rounded-md px-3 py-2 text-xs">
          <TriangleAlert className="text-warning mt-0.5 size-3.5 shrink-0" aria-hidden />
          <span>
            CSS can break layouts for every customer. Prefer the theme controls above, and check the
            preview in both schemes and on a phone before publishing.
          </span>
        </div>
        <FormField
          label="CSS"
          error={cssError}
          description={`${draft.site.customCss.length.toLocaleString()} of ${MAX_CUSTOM_CSS.toLocaleString()} characters`}
        >
          <Textarea
            value={draft.site.customCss}
            onChange={(e) =>
              draft.update((s) => {
                s.customCss = e.target.value;
              }, 'Edit custom CSS')
            }
            maxLength={MAX_CUSTOM_CSS}
            rows={10}
            spellCheck={false}
            placeholder={'.hearth-custom [data-slot="card"] {\n  border-width: 2px;\n}'}
            className="min-h-40 font-mono text-xs"
          />
        </FormField>
      </ControlSection>
    </>
  );

  return (
    <StudioPanes
      title="Theme"
      description="Colours, type and the shape of things, for dark and light mode. The preview is the real components with your theme applied."
      controls={controls}
      previewLabel={`Live preview · ${scheme}`}
      previewActions={
        <div
          className="bg-muted inline-flex rounded-lg p-[3px]"
          role="group"
          aria-label="Preview theme"
        >
          {(['dark', 'light'] as const).map((s) => (
            <Button
              key={s}
              type="button"
              size="xs"
              variant={scheme === s ? 'outline' : 'ghost'}
              aria-pressed={scheme === s}
              onClick={() => setScheme(s)}
            >
              {s === 'dark' ? <Moon /> : <Sun />} {s === 'dark' ? 'Dark' : 'Light'}
            </Button>
          ))}
        </div>
      }
      preview={
        <div className="grid gap-3">
          <PreviewFrame>
            <ThemePreview
              theme={theme}
              scheme={scheme}
              name={displayName(site, session.panel.name)}
              logo={pickLogo(site, scheme, 'sidebar')}
            />
          </PreviewFrame>
          {errors.length > 0 && (
            <p className="text-destructive flex items-start gap-2 text-xs" role="alert">
              <CircleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
              Text would be unreadable on the main surfaces; the draft can be saved but not
              published until this is fixed.
            </p>
          )}
        </div>
      }
    />
  );
}

/** A preset as a strip of its colours, dark half and light half. */
function PresetSwatch({
  label,
  light,
  dark,
  onClick,
}: {
  label: string;
  light: Record<PaletteKey, string>;
  dark: Record<PaletteKey, string>;
  onClick: () => void;
}) {
  const strip = (p: Record<PaletteKey, string>) => (
    <span className="flex h-6 flex-1 overflow-hidden rounded-md border">
      {(['background', 'sidebar', 'card', 'primary'] as PaletteKey[]).map((k) => (
        <span key={k} className="flex-1" style={{ backgroundColor: p[k] }} />
      ))}
    </span>
  );
  return (
    <button
      type="button"
      onClick={onClick}
      className="hover:border-foreground/30 hover:bg-accent focus-visible:ring-ring/50 grid w-full gap-1.5 rounded-lg border p-2 text-left transition-colors outline-none focus-visible:ring-[3px]"
    >
      <span className="flex gap-1">
        {strip(dark)}
        {strip(light)}
      </span>
      <span className="text-xs font-medium">{label}</span>
    </button>
  );
}

function ContrastNotice({ warning, error }: { warning: ContrastWarning; error: boolean }) {
  const [a, b] = warning.pair;
  const Icon = error ? CircleAlert : TriangleAlert;
  return (
    <li
      className={cn(
        'flex items-start gap-2 rounded-md px-3 py-2 text-xs',
        error ? 'bg-destructive/10 text-destructive' : 'bg-warning/10 text-foreground',
      )}
    >
      <Icon className={cn('mt-0.5 size-3.5 shrink-0', !error && 'text-warning')} aria-hidden />
      <span>
        <strong>{PALETTE_LABELS[a]}</strong> on <strong>{PALETTE_LABELS[b]}</strong> is{' '}
        {warning.ratio.toFixed(1)}:1; aim for {warning.minimum}:1.
        {error && ' Publishing is refused below 3:1.'}
      </span>
    </li>
  );
}

function FontSelect({
  label,
  value,
  kinds,
  onChange,
  sample,
}: {
  label: string;
  value: FontId;
  kinds: ('sans' | 'serif' | 'mono')[];
  onChange: (value: FontId) => void;
  sample: string;
}) {
  const options = FONT_OPTIONS.filter((f) => (kinds as string[]).includes(f.kind));
  return (
    <div className="grid gap-2">
      <FormField label={label}>
        <Select value={value} onValueChange={(v) => onChange(v as FontId)}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((f) => (
              <SelectItem key={f.id} value={f.id}>
                <span style={{ fontFamily: f.stack }}>{f.label}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>
      <p
        className="bg-elevated/60 truncate rounded-md border px-3 py-2 text-base"
        style={{ fontFamily: fontStack(value) }}
        aria-hidden
      >
        {sample}
      </p>
    </div>
  );
}

/** A segmented choice; the options are few and all visible. */
function Choice<T extends string>({
  label,
  description,
  value,
  options,
  onChange,
}: {
  label: string;
  description?: ReactNode;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <div className="grid gap-1.5">
      <p className="text-sm leading-none font-medium">{label}</p>
      {description && <p className="text-muted-foreground text-xs">{description}</p>}
      <div
        className="bg-muted inline-flex w-full flex-wrap rounded-lg p-[3px]"
        role="group"
        aria-label={label}
      >
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            aria-pressed={value === o.value}
            onClick={() => onChange(o.value)}
            className={cn(
              'focus-visible:ring-ring/50 h-7 flex-1 rounded-md px-2 text-xs font-medium whitespace-nowrap transition-colors outline-none focus-visible:ring-[3px]',
              value === o.value
                ? 'bg-background text-foreground shadow-sm dark:bg-input/30 dark:border-input dark:border'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}
